import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ArrowRight, DollarSign, Users, Zap, CheckCircle2, Minus, Plus, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Logo } from '../components/Logo';
import { FestivalCreationHeader } from '../components/FestivalCreationHeader';

interface StandDetail {
  dimensionType: string;
  customDimension: string;
  hasElectricity: boolean;
  budget: number;
  isFree: boolean;
  note: string;
  otherCategory?: string;
}

export default function CreateFestivalBusinessModelPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [standCount, setStandCount] = useState<number | null>(null);
  const [showCustomStandCount, setShowCustomStandCount] = useState(false);
  const [customStandCount, setCustomStandCount] = useState<number | string>('');
  const [formData, setFormData] = useState({
    standTypes: [] as string[],
    standCounts: {} as Record<string, number>,
    standDetails: {} as Record<string, StandDetail>,
    acceptFestinModel: true,
    expectedPriceRange: '' as string,
  });

  // Mêmes 4 paliers que stand.priceRange (MyStandPage) — comparabilité directe
  // pour le matching (critère "positionnement prix").
  const PRICE_POSITIONING = [
    { id: 'budget',  label: '€ · < 10€' },
    { id: 'medium',  label: '€€ · 10–20€' },
    { id: 'premium', label: '€€€ · 20–40€' },
    { id: 'luxury',  label: '€€€€ · > 40€' },
  ];

  const STAND_TYPES = [
    { id: 'food', label: 'Food Truck' },
    { id: 'vinyles', label: 'Vinyles' },
    { id: 'art', label: 'Artisanat / Créateurs' },
    { id: 'fashion', label: 'Mode / Friperie / Vêtements' },
    { id: 'tattoo', label: 'Tatouage / Piercing / Paillettes' },
    { id: 'associative', label: 'Engagé / Associatif' },
    { id: 'partners', label: 'Partenaires / Sponsors' },
    { id: 'service', label: 'Services / Bien-être' },
    { id: 'other', label: 'Autre' }
  ];

  useEffect(() => {
    const fetchFestival = async () => {
      if (!user) return;
      const activeId = sessionStorage.getItem('activeFestivalId') || user.uid;
      try {
        const docRef = doc(db, 'festivals', activeId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const bizModel = data.businessModel || {};
          
          if (bizModel.standCount) {
             setStandCount(bizModel.standCount);
             if (![5, 10, 15, 20].includes(bizModel.standCount)) {
               setShowCustomStandCount(true);
               setCustomStandCount(bizModel.standCount);
             }
          }
          
          setFormData({
            standTypes: bizModel.standTypes || [],
            standCounts: bizModel.standCounts || {},
            standDetails: bizModel.standDetails || {},
            acceptFestinModel: bizModel.acceptFestinModel ?? true,
            expectedPriceRange: data.expectedPriceRange || bizModel.expectedPriceRange || '',
          });
        }
      } catch (error) {
        console.error("Error fetching business model details", error);
      }
    };
    fetchFestival();
  }, [user]);

  const toggleStandType = (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setFormData(prev => {
      const isSelected = prev.standTypes.includes(id);
      let newCounts = { ...prev.standCounts };
      let newTypes = [...prev.standTypes];
      let newDetails = { ...prev.standDetails };
      
      if (isSelected) {
        newTypes = newTypes.filter(t => t !== id);
        delete newCounts[id];
        delete newDetails[id];
      } else {
        newTypes.push(id);
        newCounts[id] = 1;
        newDetails[id] = { dimensionType: '3×3m', customDimension: '', hasElectricity: false, budget: 500, isFree: false, note: '' };
      }
      
      return { ...prev, standTypes: newTypes, standCounts: newCounts, standDetails: newDetails };
    });
  };

  const updateCount = (id: string, delta: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setFormData(prev => {
      const currentCount = prev.standCounts[id] || 0;
      const newCount = Math.max(0, currentCount + delta);
      
      let newTypes = [...prev.standTypes];
      let newCounts = { ...prev.standCounts };
      let newDetails = { ...prev.standDetails };
      
      if (newCount > 0) {
        if (!newTypes.includes(id)) {
            newTypes.push(id);
            newDetails[id] = { dimensionType: '3×3m', customDimension: '', hasElectricity: false, budget: 500, isFree: false, note: '', otherCategory: '' };
        }
        newCounts[id] = newCount;
      } else {
        newTypes = newTypes.filter(t => t !== id);
        delete newCounts[id];
        delete newDetails[id];
      }
      
      return { ...prev, standTypes: newTypes, standCounts: newCounts, standDetails: newDetails };
    });
  };

  const updateDetail = (id: string, field: keyof StandDetail, value: any) => {
    setFormData(prev => ({
      ...prev,
      standDetails: {
        ...prev.standDetails,
        [id]: {
          ...(prev.standDetails[id] || { dimensionType: '3×3m', customDimension: '', hasElectricity: false, budget: 500, isFree: false, note: '', otherCategory: '' }),
          [field]: value
        }
      }
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setIsSaving(true);
    const activeId = sessionStorage.getItem('activeFestivalId') || user.uid;
    try {
      await setDoc(doc(db, 'festivals', activeId), {
        totalStands: standCount || 0,
        standTypes: formData.standTypes,
        standCounts: formData.standCounts,
        standDetails: formData.standDetails,
        expectedPriceRange: formData.expectedPriceRange,
        businessModel: { ...formData, standCount }
      }, { merge: true });
      navigate('/create-festival/step-4');
    } catch (error) {
      console.error("Error saving business model details", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#121212] text-white font-sans relative pb-24">
      <FestivalCreationHeader onBack={() => navigate(-1)} />

      <main className="max-w-4xl mx-auto px-4 md:px-6 pt-4">
        <div className="mb-8 md:mb-12">
          <div className="flex items-center justify-between text-xs font-black text-gray-500 mb-4 uppercase tracking-[0.2em]">
            <span>Business Model • Étape 5/7</span>
            <span className="text-[#ff5a00]">Revenus & Stands</span>
          </div>
          <div className="grid grid-cols-7 gap-1.5 md:gap-2">
            {[1, 2, 3, 4, 5, 6, 7].map((step) => (
              <div
                key={step}
                className={`h-1 md:h-1.5 rounded-full transition-all duration-500 ${
                  step <= 5
                    ? 'bg-[#FF5E00] shadow-[0_0_10px_rgba(255,94,0,0.4)]'
                    : 'bg-white/5'
                }`}
              />
            ))}
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-6 md:space-y-8">
          
          {/* Bloc 0: Nombre de Stands */}
          <div className="bg-black/40 border border-white/10 rounded-[24px] md:rounded-3xl p-5 md:p-8 shadow-2xl relative overflow-hidden">
             <h2 className="text-lg md:text-xl font-bold text-white uppercase tracking-tighter mb-6">Combien de stands prévoyez-vous pour ce festival ?</h2>
             <div className="flex flex-wrap gap-3">
               {[5, 10, 15, 20].map((count) => (
                 <button
                   type="button"
                   key={count}
                   onClick={() => {
                     setStandCount(count);
                     setShowCustomStandCount(false);
                     setCustomStandCount('');
                   }}
                   className={`px-8 py-4 rounded-xl border font-bold text-sm uppercase tracking-widest transition-all ${
                     standCount === count && !showCustomStandCount
                     ? 'bg-[#FF5C00] border-[#FF5C00] text-white'
                     : 'bg-white/5 border-white/10 text-white hover:border-white/20'
                   }`}
                 >
                   {count}
                 </button>
               ))}
               <button
                 type="button"
                 onClick={() => {
                   setShowCustomStandCount(true);
                   setStandCount(Number(customStandCount) || 0);
                 }}
                 className={`px-8 py-4 rounded-xl border font-bold text-sm uppercase tracking-widest transition-all ${
                   showCustomStandCount
                   ? 'bg-[#FF5C00] border-[#FF5C00] text-white'
                   : 'bg-white/5 border-white/10 text-white hover:border-white/20'
                 }`}
               >
                 Autre
               </button>
             </div>
             
             {showCustomStandCount && (
               <div className="mt-6">
                 <label className="block font-mono text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-2">Nombre précis (1-200)</label>
                 <input 
                   type="number" 
                   min="1" 
                   max="200"
                   value={customStandCount}
                   onChange={(e) => {
                     const val = Number(e.target.value);
                     setCustomStandCount(e.target.value);
                     setStandCount(val);
                   }}
                   className="w-[120px] bg-black/40 border border-white/10 rounded-xl px-5 py-4 focus:outline-none focus:border-[#FF5C00] transition-colors text-lg"
                 />
               </div>
             )}
          </div>

          {/* Positionnement prix attendu — distinct du droit d'entrée du stand
              (budget ci-dessus/ci-dessous) : c'est ce que VOS FESTIVALIERS
              dépensent en moyenne chez les exposants. Comparé directement à la
              "Fourchette de prix" que chaque exposant déclare sur son profil,
              pour affiner les suggestions "Pour vous" (ex. un festival bohème /
              petit budget matche mieux avec des exposants aux articles
              abordables qu'avec un exposant haut de gamme). */}
          <div className="bg-black/40 border border-white/10 rounded-[24px] md:rounded-3xl p-5 md:p-8 shadow-2xl relative overflow-hidden">
            <h2 className="text-lg md:text-xl font-bold text-white uppercase tracking-tighter mb-2">Quel budget moyen vos festivaliers dépensent-ils sur les stands ?</h2>
            <p className="text-sm text-gray-500 mb-6">Panier moyen estimé chez un exposant (nourriture, article, prestation...). Aide à faire matcher les bons exposants avec votre public.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {PRICE_POSITIONING.map((range) => (
                <button key={range.id} type="button"
                  onClick={() => setFormData(p => ({ ...p, expectedPriceRange: p.expectedPriceRange === range.id ? '' : range.id }))}
                  className={`py-4 px-3 rounded-2xl border text-[11px] font-black uppercase tracking-widest transition-all ${
                    formData.expectedPriceRange === range.id
                      ? 'bg-[#FF5C00] border-[#FF5C00] text-white shadow-lg shadow-[#FF5C00]/20'
                      : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                  }`}
                >{range.label}</button>
              ))}
            </div>
          </div>

          <div className="bg-black/40 border border-white/10 rounded-[24px] md:rounded-3xl p-5 md:p-8 space-y-5 md:space-y-6">
            <h2 className="text-lg md:text-xl font-bold mb-4 flex items-center shrink-0">
               <Users className="mr-2 md:mr-3 text-[#FF5C00] w-5 h-5 md:w-6 md:h-6"/> Types de stands recherchés
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
               {STAND_TYPES.map(type => {
                 const isSelected = formData.standTypes.includes(type.id);
                 const count = formData.standCounts[type.id] || 0;
                 
                 return (
                   <div 
                     key={type.id}
                     onClick={(e) => toggleStandType(type.id, e)}
                     className={`relative p-2.5 md:p-3 rounded-xl md:rounded-2xl border transition-all cursor-pointer flex flex-col items-center text-center justify-between min-h-[90px] md:min-h-[100px] ${
                       isSelected
                         ? 'bg-[#FF5C00]/10 border-[#FF5C00] text-white'
                         : 'bg-white/5 border-white/10 text-gray-500 hover:border-white/20'
                     }`}
                   >
                     <div className="text-[9px] md:text-[10px] font-black uppercase tracking-widest mb-2 opacity-80 leading-tight">{type.label}</div>
                     
                     {isSelected ? (
                       <div className="flex flex-col items-center w-full mt-auto">
                         <div className="flex items-center justify-center space-x-2 md:space-x-3 bg-black/40 rounded-lg md:rounded-xl p-1 w-full border border-white/5">
                           <button 
                             type="button" 
                             onClick={(e) => updateCount(type.id, -1, e)}
                             className="p-1 md:p-1.5 hover:bg-white/5 rounded transition-colors text-gray-400 hover:text-white"
                           >
                             <Minus className="w-3 h-3" />
                           </button>
                           <span className="text-xs md:text-sm font-black text-[#FF5C00] min-w-[1rem] tracking-tighter">{count}</span>
                           <button 
                             type="button" 
                             onClick={(e) => updateCount(type.id, 1, e)}
                             className="p-1 md:p-1.5 hover:bg-white/5 rounded transition-colors text-[#FF5C00]"
                           >
                             <Plus className="w-3 h-3" />
                           </button>
                         </div>
                       </div>
                     ) : (
                       <Plus className="w-3 h-3 md:w-4 md:h-4 mt-auto mb-1 opacity-20" />
                     )}
                   </div>
                 );
               })}
            </div>
          </div>

          <div className="bg-black/40 border border-white/10 rounded-[24px] md:rounded-3xl p-5 md:p-8 space-y-5 md:space-y-6">
            <h2 className="text-lg md:text-xl font-bold mb-2 md:mb-4 flex items-center shrink-0">
               <Users className="mr-2 md:mr-3 text-[#FF5C00] w-5 h-5 md:w-6 md:h-6"/> Gestion des emplacements
            </h2>
            
            {/* Barre de progression */}
            <div className="mb-6">
              <div className="flex justify-between text-xs font-black text-gray-500 uppercase tracking-widest mb-2">
                <span>Configuration des stands</span>
                <span>{Object.values(formData.standCounts).reduce((a, b) => a + b, 0)} / {standCount || 0} stands configurés</span>
              </div>
              <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#FF5C00] transition-all duration-500"
                  style={{ width: `${Math.min(100, ((Object.values(formData.standCounts).reduce((a, b) => a + b, 0)) / (standCount || 1)) * 100)}%` }}
                />
              </div>
            </div>

            <div className="space-y-4">
              {formData.standTypes.map((standId) => {
                const standType = STAND_TYPES.find(t => t.id === standId);
                if (!standType) return null;
                const count = formData.standCounts[standId] || 0;
                
                return Array.from({ length: count }).map((_, idx) => {
                  const instanceId = `${standId}-${idx}`;
                  const details: StandDetail = formData.standDetails[instanceId] || { dimensionType: '3×3m', customDimension: '', hasElectricity: false, budget: 500, isFree: false, note: '', otherCategory: '' };
                  
                  return (
                    <div key={instanceId} className="p-4 md:p-5 bg-white/5 border border-white/10 rounded-xl md:rounded-2xl space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="font-bold text-white uppercase text-sm">{standType.label} #{idx + 1}</h3>
                      </div>
                      
                      {/* Dimensions */}
                      <div>
                        <label className="block text-[10px] font-black text-gray-500 mb-2 uppercase tracking-widest">Dimensions</label>
                        <select 
                          value={details.dimensionType}
                          onChange={(e) => updateDetail(instanceId, 'dimensionType', e.target.value)}
                          className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#FF5C00]"
                        >
                          {['2×2m', '3×2m', '3×3m', '4×2m', 'Sur-mesure'].map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                        {details.dimensionType === 'Sur-mesure' && (
                          <input type="text" value={details.customDimension} onChange={(e) => updateDetail(instanceId, 'customDimension', e.target.value)} placeholder="Ex: 5x2m" className="w-full mt-2 bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#FF5C00]" />
                        )}
                      </div>

                      {standId === 'other' && (
                        <div>
                          <label className="block text-[10px] font-black text-gray-500 mb-2 uppercase tracking-widest">Précisez le type</label>
                          <input 
                            type="text" 
                            value={details.otherCategory || ''} 
                            onChange={(e) => updateDetail(instanceId, 'otherCategory', e.target.value)} 
                            placeholder="Ex: Stand vinyles, etc." 
                            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#FF5C00]" 
                          />
                        </div>
                      )}

                      {/* Électricité */}
                      <div 
                        onClick={() => updateDetail(instanceId, 'hasElectricity', !details.hasElectricity)}
                        className={`p-4 rounded-xl border cursor-pointer transition-all ${details.hasElectricity ? 'bg-[#FF5C00]/20 border-[#FF5C00]' : 'bg-black/40 border-white/10'}`}
                      >
                         <div className="flex items-center text-sm font-bold text-white">
                            <Zap className={`w-4 h-4 mr-2 ${details.hasElectricity ? 'text-[#FF5C00]' : 'text-gray-500'}`} />
                            Besoin d'électricité ?
                         </div>
                      </div>

                      {/* Budget */}
                      <div 
                        onClick={() => updateDetail(instanceId, 'isFree', !details.isFree)}
                        className={`p-4 mb-4 rounded-xl border cursor-pointer transition-all ${details.isFree ? 'bg-green-500/20 border-green-500' : 'bg-black/40 border-white/10'}`}
                      >
                         <div className="flex items-center text-sm font-bold text-white">
                            <CheckCircle2 className={`w-4 h-4 mr-2 ${details.isFree ? 'text-green-500' : 'text-gray-500'}`} />
                            Emplacement gratuit ?
                         </div>
                      </div>

                      {!details.isFree && (
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Budget stand</label>
                            <span className="text-[#FF5C00] font-black text-sm">{details.budget}€</span>
                          </div>
                          <input 
                            type="range" min="0" max="1000" step="25"
                            value={details.budget}
                            onChange={(e) => updateDetail(instanceId, 'budget', Number(e.target.value))}
                            className="w-full h-2 bg-black/40 rounded-lg appearance-none cursor-pointer accent-[#FF5C00]"
                          />
                        </div>
                      )}
                      
                      {/* Note */}
                      <div>
                        <label className="block text-[10px] font-black text-gray-500 mb-2 uppercase tracking-widest">Note interne (optionnel)</label>
                        <textarea 
                           value={details.note}
                           onChange={(e) => updateDetail(instanceId, 'note', e.target.value)}
                           placeholder="Ex: food truck bienvenu, camion max 6m"
                           className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#FF5C00]" rows={2}
                        />
                      </div>
                    </div>
                  );
                });
              })}
              {formData.standTypes.length === 0 && (
                <p className="text-gray-500 text-sm italic">Veuillez sélectionner au moins un type de stand pour définir les dimensions et les coûts.</p>
              )}
            </div>
          </div>

          <div className="bg-black/40 border border-white/10 rounded-[24px] md:rounded-3xl p-5 md:p-8 space-y-4">
            <h2 className="text-lg md:text-xl font-bold mb-2 md:mb-4 flex items-center shrink-0"><DollarSign className="mr-2 md:mr-3 text-[#FF5C00] w-5 h-5 md:w-6 md:h-6"/> Modèle économique pour Festin</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div 
                onClick={() => setFormData(prev => ({ ...prev, acceptFestinModel: true }))}
                className={`p-5 rounded-2xl border cursor-pointer ${formData.acceptFestinModel ? 'bg-[#FF5C00]/20 border-[#FF5C00]' : 'bg-white/5 border-white/10'}`}
              >
                  <p className="font-bold text-white text-sm mb-1">Commission Festin</p>
                  <p className="text-xs text-gray-400">Festin prend 30% du montant total des stands.</p>
              </div>
              <div 
                onClick={() => setFormData(prev => ({ ...prev, acceptFestinModel: false }))}
                className={`p-5 rounded-2xl border cursor-pointer ${!formData.acceptFestinModel ? 'bg-[#FF5C00]/20 border-[#FF5C00]' : 'bg-white/5 border-white/10'}`}
              >
                  <p className="font-bold text-white text-sm mb-1">Gratuit pour les stands</p>
                  <p className="text-xs text-gray-400">L'exposant paie un droit d'entrée.</p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-[24px] md:rounded-3xl p-6 md:p-8 mt-6">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <button type="button" onClick={() => navigate(-1)} className="w-full md:w-auto px-8 py-4 rounded-xl md:rounded-2xl border border-white/10 text-white font-bold hover:bg-white/10 transition-all text-sm">Précédent</button>
              <p className="text-[10px] md:text-xs text-gray-500 font-medium text-center hidden md:block">La logistique et les accès seront configurés ensuite.</p>
              <button type="submit" disabled={isSaving || !standCount || standCount === 0} className={`w-full md:w-auto bg-white text-black font-black py-4 px-10 md:px-12 rounded-xl md:rounded-2xl hover:bg-[#ff5e00] hover:text-white transition-all active:scale-95 flex items-center justify-center gap-2 cursor-pointer ${isSaving || !standCount || standCount === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                {isSaving ? 'ENREGISTREMENT...' : 'CONTINUER'}
                {!isSaving && <ArrowRight className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
